import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import OrdersByDayChart from './Charts/OrdersByDayChart';
import OrdersByTableChart from './Charts/OrdersByTableChart';
import OrdersByServerChart from './Charts/OrdersByServerChart';
import TicketViewer from './Charts/TicketViewer';
import { apiFetch } from '../../../../utils/api';
import KPI from './KPI'
import RankedKPI from './RankedKPI';
import Table from './Table';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toYmdDayNumber = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const s = String(isoString).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return s;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour24 = Number(m[4]);
  const minute = Number(m[5]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return s;
  if (!Number.isFinite(hour24) || !Number.isFinite(minute)) return s;
  if (month < 1 || month > 12) return s;

  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = (hour24 % 12) || 12;
  return `${MONTHS[month - 1]} ${day}, ${year}, ${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ampm}`;
};

const formatYmdDate = (ymd) => {
  if (!ymd) return '-';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '-';
  if (m < 1 || m > 12) return '-';
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};


const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-gray-900 p-6 rounded shadow-lg border border-gray-200 dark:border-gray-700 max-w-3xl w-full relative">
      <button
        className="absolute top-2 right-2 text-gray-600 dark:text-gray-300 hover:text-red-500"
        onClick={onClose}
      >
        <Icon icon="fluent-emoji:multiply" className="h-5 w-5" aria-hidden="true" />
      </button>
      {children}
    </div>
  </div>
);

const OrdersChartsSection = ({
  isDark = false,
  filters = {},
  ordersByDay = [],
  ordersByTable = [],
  ordersByServer = [],
  ordersSummary = { total_orders: 0 },
  ordersList = [],
  ordersItemsCount = [],
  ordersBumpMinutes = []
}) => {
  const [search, setSearch] = useState('');
  const [ticketDetail, setTicketDetail] = useState(null);

  // Build item count map
  const itemCountsMap = {};
  (Array.isArray(ordersItemsCount) ? ordersItemsCount : []).forEach(({ order_id, item_count, bumped_at }) => {
    itemCountsMap[order_id] = { item_count, bumped_at };
  });

  // Build time performance map
  const bumpMinutesMap = {};

  (Array.isArray(ordersBumpMinutes) ? ordersBumpMinutes : []).forEach(
    ({ order_id, bump_minutes }) => {
      bumpMinutesMap[order_id] = Number(bump_minutes);
    }
  );

  // Average / median bump time
  const bumpValues = Object.values(bumpMinutesMap)
    .filter((n) => Number.isFinite(n) && n >= 0);

  const avgBumpNum = bumpValues.length
    ? bumpValues.reduce((sum, n) => sum + n, 0) / bumpValues.length
    : null;

  const avgTicketBumpTime =
    avgBumpNum !== null ? avgBumpNum.toFixed(2) : "-";

  const medianBumpNum = (() => {
    if (!bumpValues.length) return null;

    const sorted = [...bumpValues].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    return sorted.length % 2 === 1
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  })();

  const medianTicketBumpTime =
    medianBumpNum !== null ? medianBumpNum.toFixed(2) : "-";

  // Filter for search box
  const filteredOrders = (ordersList || []).filter((order) => {
    const term = search.trim().toLowerCase();
    return (
      order.order_id?.toString().includes(term) ||
      (order.member_name && order.member_name.toLowerCase().includes(term)) ||
      (order.server_name && order.server_name.toLowerCase().includes(term)) ||
      (order.table_name && order.table_name.toLowerCase().includes(term))
    );
  });

  // Classify bump time based on average and median bump time
  const classifyBumpTime = (minutes, avg, median) => {
    if (
      !Number.isFinite(minutes) ||
      !Number.isFinite(avg) ||
      !Number.isFinite(median)
    ) {
      return null;
    }

    const lower = Math.min(avg, median);
    const upper = Math.max(avg, median);

    const reviewBuffer = 250;

    if (minutes >= lower && minutes <= upper) {
      return "Normal";
    }

    if (
      minutes >= lower - reviewBuffer &&
      minutes <= upper + reviewBuffer
    ) {
      return "Needs Review";
    }

    return "Outlier";
  };

  // Rows for Order Report Table
  const rows = filteredOrders.map((order) => ({
    ...order,

    received_atFormatted: formatDateTime(order.received_at),
    bumped_atFormatted: formatDateTime(order.bumped_at),

    items: itemCountsMap[order.order_id]?.item_count ?? 0,

    bump_minutes: bumpMinutesMap[order.order_id] ?? null,

    bumpStatus: classifyBumpTime(
      bumpMinutesMap[order.order_id],
      avgBumpNum,
      medianBumpNum
    ),
  }));


  // Columns for Order Report Table
  const columns = [
    { field: 'order_id', headerName: 'Order ID', width: 100 },
    { field: 'received_atFormatted', headerName: 'Received', width: 180 },
    { field: 'bumped_atFormatted', headerName: 'Bumped', width: 180 },
    {
      field: 'bumpStatus',
      headerName: 'Performance',
      width: 120,
      renderCell: (params) => {
        const status = params.value;
        const styles = {
          Normal: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
          'Needs Review': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
          Outlier: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        };
        if (!status) return <span className="text-gray-400">-</span>;
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || ''}`}>
            {status}
          </span>
        );
      },
    },
    { field: 'table_name', headerName: 'Table', width: 90 },
    { field: 'server_name', headerName: 'Server', flex: 1 },
    { field: 'member_name', headerName: 'Member', flex: 1 },
    { field: 'items', headerName: 'Items', width: 80 },
    {
      field: 'ticket',
      headerName: 'Ticket',
      width: 100,
      renderCell: (params) => (
        <span
          className="text-blue-600 dark:text-blue-400 underline cursor-pointer"
          onClick={async () => {
            const order = params.row;
            try {
              const detailRes = await apiFetch(
                `/updates-reports/orders-ticket-detail/${order.order_id}`
              );
              if (!detailRes.ok) {
                throw new Error(`Ticket detail request failed with HTTP ${detailRes.status}`);
              }
              const detail = await detailRes.json();
              setTicketDetail({
                ...detail,
                order_meta: {
                  order_id: order.order_id,
                  table_name: order.table_name,
                  server_name: order.server_name,
                  member_name: order.member_name,
                  received_at: order.received_at,
                  bumped_at: order.bumped_at,
                },
              });
            } catch (err) {
              console.error('Error loading ticket detail:', err);
            }
          }}
        >
          View Ticket
        </span>
      ),
      exportable: false, // column not included in the order table export
    },
  ];

  // Derived stats
  const totalItems = Object.values(itemCountsMap).reduce(
    (sum, entry) => sum + (Number(entry.item_count) || 0),
    0
  );

  // Force it to be a number, even if the API sends a string
  const totalOrders = Number(ordersSummary?.total_orders || 0);

  const avgItemsPerOrder =
    totalOrders > 0
      ? (totalItems / totalOrders).toFixed(1)
      : '-';

  const topOrdersDay = (() => {
    if (!Array.isArray(ordersByDay) || !ordersByDay.length) return null;

    const inSelectedRange = (row) => {
      const day = String(row?.date || '').slice(0, 10);
      if (!day) return false;
      if (filters?.startDate && day < String(filters.startDate)) return false;
      if (filters?.endDate && day > String(filters.endDate)) return false;
      return true;
    };

    const scopedDays =
      filters?.startDate || filters?.endDate
        ? ordersByDay.filter(inSelectedRange)
        : ordersByDay;

    if (!scopedDays.length) return null;

    return scopedDays.reduce((best, cur) =>
      Number(cur?.total_orders || 0) > Number(best?.total_orders || 0) ? cur : best
    );
  })();
  const topOrdersDayLabel = formatYmdDate(topOrdersDay?.date);
  const topOrdersDayCount = Number(topOrdersDay?.total_orders || 0);

  const avgOrdersPerDay = (() => {
    const total = Number(ordersSummary?.total_orders || 0);
    if (!total) return '-';
    const todayYmd = new Date().toISOString().slice(0, 10);
    const minDateStr = Array.isArray(ordersByDay) && ordersByDay.length
      ? ordersByDay.map((d) => String(d?.date || '').slice(0, 10)).filter(Boolean).sort()[0]
      : null;
    const startYmd = filters?.startDate || minDateStr || null;
    const endYmd = filters?.endDate || todayYmd;
    const startDay = toYmdDayNumber(startYmd);
    const endDay = toYmdDayNumber(endYmd);
    if (!Number.isFinite(startDay) || !Number.isFinite(endDay)) return '-';
    const days = Math.max(1, (endDay - startDay) + 1);
    return (total / days).toFixed(2);
  })();

  return (

    <div className="p-4 dark:text-gray-100 mb-10 space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KPI
          title="Total Orders"
          value={ordersSummary.total_orders?.toLocaleString() ?? '-'}
          color="blue"
        />
        <KPI
          title="Avg. Items per Order"
          value={avgItemsPerOrder?.toLocaleString() ?? '-'}
          color="purple"
        />
        <RankedKPI
          title="Top Orders Day"
          label={topOrdersDayLabel}
          value={topOrdersDayCount ? topOrdersDayCount.toLocaleString() : '-'}
          color="green"
        />
        <RankedKPI
          title="Top Server"
          label={ordersByServer[0]?.server_name}
          value={ordersByServer[0]?.total_orders}
          color="yellow"
        />
        <RankedKPI
          title="Top Table"
          label={ordersByTable[0]?.table_name}
          value={ordersByTable[0]?.total_orders}
          color="indigo"
        />
        <KPI
          title="Avg. Orders / Day"
          value={avgOrdersPerDay.toLocaleString() ?? '-'}
          color="cyan"
        />
        <KPI
          title="Avg. Ticket Bump (mins)"
          value={avgTicketBumpTime.toLocaleString() ?? '-'}
          color="rose"
        />
        <KPI
          title="Median Ticket Bump (mins)"
          value={medianTicketBumpTime.toLocaleString() ?? '-'}
          color="emerald"
        />
      </div>
      {/* Order Viewer - table */}
      <Table
        title="Order Report"
        rows={rows}
        columns={columns}
        isDark={isDark}
        getRowId={(rows) => rows.order_id}

        searchable
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by Order ID, Server, Table..."

        exportable
        exportTitle="Orders Report"
        exportFileName="Orders Report"
      />

      {/* Charts */}
      <div className="mb-10 space-y-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded shadow">
          <h3 className="text-blue-700 dark:text-blue-300 font-semibold mb-2">Orders by Day</h3>
          <OrdersByDayChart data={ordersByDay} />
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded shadow">
          <h3 className="text-blue-700 dark:text-blue-300 font-semibold mb-2">Orders by Server</h3>
          <OrdersByServerChart data={ordersByServer} />
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded shadow">
          <h3 className="text-blue-700 dark:text-blue-300 font-semibold mb-2">Orders by Table</h3>
          <OrdersByTableChart data={ordersByTable} />
        </div>
      </div>


      {/* Ticket Modal */}
      {ticketDetail && (
        <Modal onClose={() => setTicketDetail(null)}>
          <div className="space-y-4 max-h-[80vh] overflow-y-auto">
            <h4 className="text-blue-700 dark:text-blue-300 font-semibold">
              Ticket Detail: Order #{ticketDetail?.order_id || ticketDetail?.order_meta?.order_id}
            </h4>
            <TicketViewer ticketDetail={ticketDetail} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OrdersChartsSection;
